import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import {
  useCreateGeoSchemeFromRequestMutation,
  useGetRequestsQuery,
  useListGeoSchemesQuery,
  useTransitionRequestMutation,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { useActiveProjectContext } from '@shared/hooks/useActiveProjectContext';
import {
  slaBadge,
  statusLabel,
  statusTone,
  priorityLabel,
  priorityTone,
  type FunctionRequest,
} from '@entities/requests';
import {
  apiErrorMessage,
  fmtDate,
  readString,
  sourceFunctionLabel,
  surveyTypePhrase,
  type GeoSchemeListItem,
} from '@entities/geodesy';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import {
  Chip,
  ErrorText,
  Muted,
  ReasonDialog,
  Row,
  Stack,
  useAction,
  useGeoObjectNames,
} from '@widgets/GeoSchemes';

const ACTIVE = new Set(['submitted', 'accepted', 'in_progress']);

const Card = styled.article<{ $overdue: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(5)}`};
  padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(5)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 3px solid
    ${({ theme, $overdue }) => ($overdue ? theme.colors.status.danger : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-wrap: balance;
`;

const Meta = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};

  b {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Quote = styled.blockquote`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(3)};
  border-left: 2px solid ${({ theme }) => theme.colors.border};
  font-size: 13.5px;
  line-height: 1.5;
  white-space: pre-wrap;
  color: ${({ theme }) => theme.colors.text.primary};
  text-wrap: pretty;
`;

const Side = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};

  @media (max-width: 720px) {
    align-items: flex-start;
  }
`;

const SchemeLink = styled(Link)`
  font-size: 13.5px;
  font-weight: 600;
`;

const QuietLink = styled(Link)`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

function dueKey(r: FunctionRequest): string {
  return r.due_date ?? '9999-12-31';
}

/**
 * Входящие заявки на съёмку (`/requests?box=incoming&function=geodesy`) — от
 * прораба и инженера ПТО. Отсюда: принять, вернуть с причиной, создать ИС по
 * заявке (backend сам переводит заявку в работу).
 */
export function GeodesyRequests() {
  const { can_write } = useAgentPermission('geodesy');
  const { activeProjectId } = useActiveProjectContext();
  const [scope, setScope] = useState<'active' | 'all'>('active');
  const { data, isLoading, error } = useGetRequestsQuery(
    {
      box: 'incoming',
      function: 'geodesy',
      ...(activeProjectId ? { project_id: activeProjectId } : {}),
    },
    { pollingInterval: 30_000 },
  );
  // ИС, уже созданные по заявкам: связь request_id → схема.
  const { data: schemesData } = useListGeoSchemesQuery({ limit: 200 });

  const byRequest = useMemo(() => {
    const m = new Map<string, GeoSchemeListItem>();
    for (const s of schemesData?.schemes ?? []) if (s.request_id) m.set(s.request_id, s);
    return m;
  }, [schemesData]);

  const all = data?.requests ?? [];
  const active = all.filter((r) => ACTIVE.has(r.status));
  const list = (scope === 'active' ? active : all)
    .slice()
    .sort((a, b) =>
      dueKey(a) === dueKey(b)
        ? b.created_at.localeCompare(a.created_at)
        : dueKey(a).localeCompare(dueKey(b)),
    );

  return (
    <Stack $gap={4}>
      <Row $gap={2} role="group" aria-label="Какие заявки показать">
        <Chip
          type="button"
          $active={scope === 'active'}
          aria-pressed={scope === 'active'}
          onClick={() => setScope('active')}
        >
          В работе и новые · {active.length}
        </Chip>
        <Chip
          type="button"
          $active={scope === 'all'}
          aria-pressed={scope === 'all'}
          onClick={() => setScope('all')}
        >
          Все · {all.length}
        </Chip>
      </Row>

      {error ? (
        <ErrorText role="alert">
          {apiErrorMessage(error) ?? 'Не удалось загрузить заявки на геодезию.'}
        </ErrorText>
      ) : isLoading ? (
        <Muted>Загрузка заявок…</Muted>
      ) : list.length === 0 ? (
        <Muted>
          {scope === 'active'
            ? 'Новых и незакрытых заявок нет. Прораб создаёт заявку в мобильном приложении, инженер ПТО — кнопкой «Нет схемы — запросить у геодезии».'
            : 'Заявок на геодезию пока не было.'}
        </Muted>
      ) : (
        list.map((r) => (
          <RequestCard
            key={r.id}
            request={r}
            scheme={byRequest.get(r.id) ?? null}
            canWrite={can_write}
          />
        ))
      )}
    </Stack>
  );
}

function RequestCard({
  request: r,
  scheme,
  canWrite,
}: {
  request: FunctionRequest;
  scheme: GeoSchemeListItem | null;
  canWrite: boolean;
}) {
  const navigate = useNavigate();
  const names = useGeoObjectNames();
  const [transition] = useTransitionRequestMutation();
  const [fromRequest] = useCreateGeoSchemeFromRequestMutation();
  const accept = useAction();
  const create = useAction();
  const reject = useAction();
  const [rejecting, setRejecting] = useState(false);

  const survey = surveyTypePhrase(readString(r.payload, 'survey_type'));
  const comment = readString(r.payload, 'comment');
  const area = readString(r.payload, 'area');
  const initiator = readString(r.payload, 'initiator_name');
  const project = names.project(r.project_id);
  const site = names.site(r.site_id);
  const sla = slaBadge(r);
  // Результат заявки — id ИС (прикрепляется при передаче схемы в ПТО).
  const schemeId = scheme?.id ?? (r.result_ref || null);
  const canCreate =
    canWrite && !schemeId && ['submitted', 'accepted', 'in_progress'].includes(r.status);
  const busy = accept.busy || create.busy || reject.busy;

  async function createScheme() {
    const created = await create.runValue(
      () => fromRequest(r.id).unwrap(),
      'Схема по заявке не создана.',
    );
    if (created) navigate(`/agents/geodesy/schemes/${created.id}`);
  }

  async function doReject(reason: string) {
    const ok = await reject.run(
      () => transition({ id: r.id, action: 'reject', reason }).unwrap(),
      'Заявку не удалось вернуть.',
    );
    if (ok) setRejecting(false);
  }

  return (
    <Card $overdue={sla?.tone === 'danger'} aria-labelledby={`req-${r.id}`}>
      <Stack $gap={2}>
        <Head>
          <Title id={`req-${r.id}`}>{r.title}</Title>
          <Badge $tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
          {sla && <Badge $tone={sla.tone}>{sla.label}</Badge>}
          {r.priority !== 'normal' && (
            <Badge $tone={priorityTone(r.priority)}>{priorityLabel(r.priority)}</Badge>
          )}
        </Head>
        <Meta>
          {survey && (
            <>
              <b>{survey}</b> ·{' '}
            </>
          )}
          {r.due_date ? (
            <>
              срок <b>{fmtDate(r.due_date)}</b> ·{' '}
            </>
          ) : (
            'без срока · '
          )}
          от: {initiator ?? sourceFunctionLabel(r.source_function)}
          {project && <> · объект: {project}</>}
          {site && <> · площадка: {site}</>}
          {area && <> · участок: {area}</>}
          <br />
          поступила {fmtDate(r.created_at)}
        </Meta>
        {comment && <Quote>{comment}</Quote>}
        {(accept.error || create.error) && (
          <ErrorText role="alert">{accept.error ?? create.error}</ErrorText>
        )}
      </Stack>

      <Side>
        {schemeId ? (
          <SchemeLink to={`/agents/geodesy/schemes/${schemeId}`}>
            {scheme ? `Открыть схему ${scheme.number}` : 'Открыть схему по заявке'} →
          </SchemeLink>
        ) : (
          canCreate && (
            <Button
              type="button"
              $variant="primary"
              $size="sm"
              onClick={() => void createScheme()}
              disabled={busy}
            >
              {create.busy ? 'Создание…' : 'Создать ИС по заявке'}
            </Button>
          )
        )}
        {canWrite && r.status === 'submitted' && (
          <Row $gap={2}>
            <Button
              type="button"
              $variant="secondary"
              $size="sm"
              onClick={() =>
                void accept.run(
                  () => transition({ id: r.id, action: 'accept' }).unwrap(),
                  'Заявку не удалось принять.',
                )
              }
              disabled={busy}
            >
              {accept.busy ? 'Принимаем…' : 'Принять'}
            </Button>
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              onClick={() => setRejecting(true)}
              disabled={busy}
            >
              Отклонить
            </Button>
          </Row>
        )}
        <QuietLink to={`/requests/${r.id}`}>Карточка заявки</QuietLink>
      </Side>

      {rejecting && (
        <ReasonDialog
          title="Отклонить заявку"
          hint="Заявка вернётся инициатору с причиной — он сможет поправить и подать её повторно."
          label="Причина (обязательно)"
          confirmLabel="Отклонить"
          danger
          busy={reject.busy}
          error={reject.error}
          onConfirm={(reason) => void doReject(reason)}
          onClose={() => setRejecting(false)}
        />
      )}
    </Card>
  );
}
